import { startAuthentication } from "@simplewebauthn/browser";
import { z } from "zod";

import type { ForensicRecoveryPath } from "@shared/forensic-recovery-status";
import { apiRequest } from "@/lib/queryClient";
import { decodePrfExtensionInputs } from "@/lib/webauthn-prf-extension";
import type { WardLiftChallenge } from "@/lib/forensic-recovery-lift-challenge";

type AuthenticationOptions = Parameters<typeof startAuthentication>[0]["optionsJSON"];
type EvidenceSlot = "1" | "2";

const attemptResponseSchema = z.object({ attemptId: z.string().uuid() }).passthrough();
const guardianResponseSchema = z.object({ recoveryAttemptId: z.string().uuid() }).passthrough();
const liftResponseSchema = z.object({ status: z.literal("lifted"), requiresFreshSignIn: z.literal(true) }).strict();

function postOptions(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

/**
 * Starts only a server-approved path and returns its opaque attempt UUID.
 *
 * `challenge` is the ward (requester) leg's question-gated proof (#3042 Part
 * B) -- omitted, the request body is byte-identical to the pre-#3042 shape;
 * supplied, it rides the exact call the server gates (`initiate` for
 * guardian_quorum, the `guardian-ceremony` POST for key_guardian).
 */
export async function startForensicRecoveryPath(
  path: ForensicRecoveryPath,
  challenge?: WardLiftChallenge,
): Promise<string> {
  if (path === "guardian_quorum") {
    const response = await apiRequest("/api/circle/handshake/initiate", postOptions({
      operationType: "forensic_pause_lift",
      ...(challenge ?? {}),
    }));
    return guardianResponseSchema.parse(response).recoveryAttemptId;
  }
  const created = attemptResponseSchema.parse(await apiRequest(
    "/api/security/forensic-recovery/attempts",
    postOptions({ path }),
  ));
  if (path === "key_guardian") {
    await apiRequest(
      `/api/security/forensic-recovery/attempts/${created.attemptId}/guardian-ceremony`,
      postOptions(challenge ?? {}),
    );
  }
  return created.attemptId;
}

/** Idempotently binds or returns the exact guardian ceremony for a mixed attempt. */
export async function retryForensicRecoveryGuardianCeremony(
  attemptId: string,
  challenge?: WardLiftChallenge,
): Promise<void> {
  guardianResponseSchema.parse(await apiRequest(
    `/api/security/forensic-recovery/attempts/${attemptId}/guardian-ceremony`, postOptions(challenge ?? {}),
  ));
}

/** Keeps the WebAuthn assertion on the stack between exact begin and finish calls. */
export async function collectForensicRecoveryPasskey(
  attemptId: string,
  evidenceSlot: EvidenceSlot,
): Promise<void> {
  const begin = await apiRequest<AuthenticationOptions>(
    `/api/security/forensic-recovery/attempts/${attemptId}/passkey/begin`,
    postOptions({}),
  );
  const assertionResponse = await startAuthentication({
    optionsJSON: decodePrfExtensionInputs(begin),
  });
  await apiRequest(
    `/api/security/forensic-recovery/attempts/${attemptId}/passkey/finish`,
    postOptions({ evidenceSlot, assertionResponse }),
  );
}

/** Submits an offline code directly without placing it in query or mutation state. */
export async function submitForensicRecoveryCode(attemptId: string, code: string): Promise<void> {
  await apiRequest(
    `/api/security/forensic-recovery/attempts/${attemptId}/offline-code`,
    postOptions({ code }),
  );
}

/** Cancels the authenticated owner's exact live attempt. */
export async function cancelForensicRecovery(attemptId: string): Promise<void> {
  await apiRequest(
    `/api/security/forensic-recovery/attempts/${attemptId}/cancel`,
    postOptions({}),
  );
}

/** Finishes the exact ready attempt and validates committed-session-loss semantics. */
export async function finishForensicRecovery(attemptId: string): Promise<void> {
  const response = await apiRequest(
    `/api/security/forensic-recovery/attempts/${attemptId}/lift`,
    postOptions({}),
  );
  liftResponseSchema.parse(response);
}

/** Leaves the invalidated recovery session through a full document navigation. */
export function returnToSignIn(): void {
  window.location.assign("/auth");
}

/** Uses fresh UV and keeps the veto assertion stack-local. */
export async function vetoForensicRecoveryWithCredential(attemptId: string): Promise<void> {
  const begin = await apiRequest<AuthenticationOptions>(
    `/api/security/forensic-recovery/attempts/${attemptId}/veto`,
    postOptions({ method: "credential", phase: "begin" }),
  );
  const assertionResponse = await startAuthentication({ optionsJSON: decodePrfExtensionInputs(begin) });
  await apiRequest(`/api/security/forensic-recovery/attempts/${attemptId}/veto`,
    postOptions({ method: "credential", phase: "finish", assertionResponse }));
}
